import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, videos } from '@/mocks/data';

function formatViews(n: number): string {
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}千万`;
  if (n >= 10000) return `${(n / 10000).toFixed(0)}万`;
  return n.toString();
}

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = videos.find((v) => v.id === Number(id));

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <section className="pt-24 pb-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-950 mb-2">视频未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">抱歉，该视频不存在或已下架</p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-primary-500 text-white rounded-full text-sm no-underline cursor-pointer hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.category === article.category && v.id !== article.id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '芭乐视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '芭乐视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bala-video.com/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://bala-video.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: getCategoryName(article.category),
        item: `https://bala-video.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://bala-video.com/article/${article.id}`,
      },
    ],
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-20 md:pt-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer no-underline whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 cursor-pointer no-underline whitespace-nowrap">
              {getCategoryName(article.category)}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{article.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <div className="relative aspect-video rounded-xl overflow-hidden mb-6">
                <img
                  src={article.coverImageLarge}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={`${article.title} - 芭乐视频`}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group cursor-pointer">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-all duration-200 hover:scale-110">
                    <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                  </div>
                </div>
              </div>

              <h1 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-label">
                  {getCategoryName(article.category)}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-sm"></i> {article.rating}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {formatViews(article.views)}次播放
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">
                  {article.platform}
                </span>
              </div>

              <div className="prose-custom mb-8">
                <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-4">
                  {article.description}
                </p>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                  {article.content}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-10">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full border border-background-200/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <h3 className="font-heading text-base text-foreground-950 mb-4">
                  相关推荐
                </h3>
                <div className="space-y-3">
                  {relatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer no-underline"
                    >
                      <div className="w-20 h-14 md:w-24 md:h-16 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={video.thumbImage}
                          alt={video.title}
                          className="w-full h-full object-cover object-top"
                          title={`${video.title} - 芭乐视频`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-label text-foreground-900 font-medium line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h4>
                        <span className="text-xs text-accent-600 flex items-center gap-0.5">
                          <i className="ri-star-fill text-xs"></i> {video.rating}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="text-center">
          <Link
            to={`/category/${article.category}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm no-underline cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回{getCategoryName(article.category)}分类
          </Link>
        </div>
      </div>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </main>
  );
}